import React, { type ReactElement } from 'react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import NumberInput from 'atom-components/form-fields-for-hook-form/number-input';
import { UnstyledSubmitButton } from 'atom-components/form-fields-for-hook-form/submit-button';
import Label from 'atom-components/label';
import TooltipItem from 'atom-components/tooltip-item';
import { defaultPage } from 'helpers/pagination.helpers';
import styles from './controls.module.scss';

type PaginationButtonProps = {
  tooltip: string;
  icon: IconProp;
  onClick: () => void;
  disabled?: boolean;
};

function PaginationButton({
  tooltip,
  icon,
  onClick,
  disabled
}: PaginationButtonProps): ReactElement {
  return (
    <TooltipItem tooltip={tooltip}>
      <UnstyledSubmitButton
        className={styles.paginationButton}
        onClick={onClick}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={icon} size="lg" />
      </UnstyledSubmitButton>
    </TooltipItem>
  );
}

type PageNumberProps = {
  triggerFormSubmission: () => void;
  totalPages: number;
};

function PageNumber({ triggerFormSubmission, totalPages }: PageNumberProps): ReactElement {
  const inputName = 'page';
  const inputLabel = 'Page';

  return (
    <>
      <Label htmlFor={inputName} className={styles.pageNumberLabel}>
        {inputLabel}
      </Label>
      <div className={styles.pageNumberInputContainer}>
        <NumberInput
          id={inputName}
          name={inputName}
          ariaLabel={inputLabel}
          min={1}
          max={totalPages}
          onChange={(): void => triggerFormSubmission()}
        />
      </div>
      <span className={styles.totalPages}> of {totalPages}</span>
    </>
  );
}

type ControlsProps = {
  triggerFormSubmission: () => void;
  currentPage: number;
  totalPages: number;
};

export default function Controls({
  triggerFormSubmission,
  currentPage,
  totalPages
}: ControlsProps): ReactElement {
  const { setValue, formState } = useFormContext();
  const isOnFirstPage = currentPage === defaultPage;
  const isOnLastPage = currentPage === totalPages;
  const pageNavigateNumber = 1;

  return (
    <div className={styles.controlsContainer}>
      <div className={styles.controls}>
        <PaginationButton
          tooltip="First"
          icon={['fas', 'angles-left']}
          disabled={isOnFirstPage}
          onClick={(): void => setValue('page', defaultPage)}
        />
        <PaginationButton
          tooltip="Previous"
          icon={['fas', 'angle-left']}
          disabled={isOnFirstPage}
          onClick={(): void => setValue('page', currentPage - pageNavigateNumber)}
        />
        <PageNumber triggerFormSubmission={triggerFormSubmission} totalPages={totalPages} />
        <PaginationButton
          tooltip="Next"
          icon={['fas', 'angle-right']}
          disabled={isOnLastPage}
          onClick={(): void => setValue('page', currentPage + pageNavigateNumber)}
        />
        <PaginationButton
          tooltip="Last"
          icon={['fas', 'angles-right']}
          disabled={isOnLastPage}
          onClick={(): void => setValue('page', totalPages)}
        />
      </div>
      {formState.errors.page && (
        <ErrorMessage
          errors={formState.errors}
          name="page"
          containerClassName={styles.errorMessageContainer}
        />
      )}
    </div>
  );
}
